import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  Divider,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CalendarToday,
  ExpandMore,
  ChecklistRtl,
  PhotoCamera,
  Thermostat,
  Cloud,
  Add,
  Remove,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice';
import { ApiService } from '@api/index';
import { checklistsApi, Checklist as ApiChecklist, CategoryNode } from '@api/checklists';
import DateTimePicker from '@components/forms/DateTimePicker';
import ImageUploader from '@components/forms/ImageUploader';
import { StarRating, SmileRating, YesNoRating } from '@components/forms/RatingComponents';
import HiveSelector from '@components/inspections/HiveSelector';
import ApiarySelector from '@components/inspections/ApiarySelector';
import SaveToolbar from '@components/common/SaveToolbar';

interface InspectionFormData {
  hive_ids: number[];
  checklist_id: number | null;
  date: Date;
  notes: string;
  weather: string;
  temperature: number | null;
  impression: number;
  reminder: Date | null;
  reminder_notes: string;
  items: Record<string, any>;
}

const HEADER_CHECKLIST_SLUGS = new Set(['temp_c', 'weather_condition', 'temperament']);
const FRAME_SLUGS = new Set(['frames_bees', 'frames_brood', 'honey_frames', 'pollen_frames']);
const SIMPLE_LABELS: Record<string, string> = {
  bee_colony: 'Bees',
  food: 'Food',
  disorder: 'Pests',
  eggs_seen: 'Eggs?',
  larvae_seen: 'Young brood?',
  pupae_seen: 'Sealed brood?',
  brood_pattern: 'How is the brood?',
  queen_seen: 'Queen?',
  queen_cells: 'Queen cells?',
  frames_bees: 'Frames with bees',
  frames_brood: 'Frames with brood',
  honey_frames: 'Frames with honey',
  pollen_frames: 'Frames with pollen',
  varroa: 'Mites',
  shb: 'Hive beetles',
  temperament: 'How are the bees?',
};
const SIMPLE_CHOICE_LABELS: Record<string, string> = {
  none: 'None',
  light: 'Few',
  moderate: 'Some',
  heavy: 'Many',
  few: 'Few',
  many: 'Many',
  low: 'Few',
  medium: 'Some',
  high: 'Many',
};
const FALLBACK_PEST_OPTIONS: Record<string, { value: string; label: string }[]> = {
  varroa: [
    { value: 'none', label: 'None' },
    { value: 'few', label: 'Few' },
    { value: 'many', label: 'Many' },
  ],
  shb: [
    { value: 'none', label: 'None' },
    { value: 'few', label: 'Few' },
    { value: 'many', label: 'Many' },
  ],
};

const simpleName = (node: CategoryNode) => SIMPLE_LABELS[node.slug || ''] || node.name;

const collectSelectedLeaves = (node: CategoryNode, acc: CategoryNode[]) => {
  if (HEADER_CHECKLIST_SLUGS.has(node.slug || '')) return;
  if (node.state?.selected && node.input_type && node.input_type !== 'none') {
    acc.push(node);
  }
  (node.children || []).forEach((child) => collectSelectedLeaves(child, acc));
};

const InspectionCreate: React.FC = () => {
  const navigate = useNavigate();
  const { hiveId: paramHiveId } = useParams<{ hiveId: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hiveIdFromQuery = searchParams.get('hiveId');
  const hiveId = paramHiveId || hiveIdFromQuery;

  const { hives, apiaries, loadingState } = useAppSelector((state) => state.locations);
  const [loading, setLoading] = useState(false);
  const [loadingChecklists, setLoadingChecklists] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [inspectionImages, setInspectionImages] = useState<any[]>([]);
  const [checklists, setChecklists] = useState<ApiChecklist[]>([]);
  const [selectedChecklist, setSelectedChecklist] = useState<ApiChecklist | null>(null);
  const [checklistTaxonomy, setChecklistTaxonomy] = useState<CategoryNode[]>([]);
  const [selectedApiaryId, setSelectedApiaryId] = useState<number | null>(null);

  const [formData, setFormData] = useState<InspectionFormData>({
    hive_ids: hiveId ? [parseInt(hiveId, 10)] : [],
    checklist_id: null,
    date: new Date(),
    notes: '',
    weather: 'sunny',
    temperature: null,
    impression: 3,
    reminder: null,
    reminder_notes: '',
    items: {},
  });

  const selectedHives = Array.from(
    new Map(
      hives
        .filter((h) => formData.hive_ids.includes(h.id))
        .map((h) => [h.id, h]),
    ).values(),
  );

  const groupedChecklist = useMemo(
    () =>
      checklistTaxonomy
        .map((root) => {
          const leaves: CategoryNode[] = [];
          collectSelectedLeaves(root, leaves);
          return { root, leaves };
        })
        .filter((group) => group.leaves.length > 0),
    [checklistTaxonomy],
  );

  useEffect(() => {
    if (loadingState === 'idle') {
      dispatch(fetchApiariesAndGroups());
    }
  }, [dispatch, loadingState]);

  useEffect(() => {
    if (hiveId) {
      setFormData((prev) => ({ ...prev, hive_ids: [parseInt(hiveId, 10)] }));
    }
    loadChecklists();
  }, [hiveId]);

  useEffect(() => {
    if (selectedApiaryId) return;
    const hiveFromQuery = hiveId
      ? hives.find((h) => h.id === parseInt(hiveId, 10))
      : null;
    if (hiveFromQuery?.location_id) {
      setSelectedApiaryId(hiveFromQuery.location_id);
      return;
    }
    if (apiaries.length === 1) {
      setSelectedApiaryId(apiaries[0].id);
    }
  }, [hiveId, hives, apiaries, selectedApiaryId]);

  const loadChecklists = async () => {
    try {
      setLoadingChecklists(true);
      const response = await checklistsApi.getChecklists();

      if (response.success && response.data.checklists) {
        setChecklists(response.data.checklists);

        if (response.data.checklists.length > 0) {
          const firstChecklist = response.data.checklists[0];
          handleChecklistChange(firstChecklist.id);
        }
      }
    } catch (error) {
      console.error('Failed to load checklists:', error);
      setMessage({ type: 'error', text: 'Failed to load checklists' });
    } finally {
      setLoadingChecklists(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : null) : value,
    }));
  };

  const handleSelectChange = (name: string) => (e: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: e.target.value,
    }));
  };

  const handleDateChange = (name: keyof InspectionFormData) => (date: Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date,
    }));
  };

  const handleChecklistChange = async (checklistId: number) => {
    try {
      setLoadingChecklists(true);
      const response = await checklistsApi.getChecklist(checklistId);

      if (response.success && response.data) {
        const checklist = response.data;
        setSelectedChecklist(checklist);
        setChecklistTaxonomy(checklist.taxonomy || []);

        setFormData((prev) => ({
          ...prev,
          checklist_id: checklistId,
          items: {},
        }));
      }
    } catch (error) {
      console.error('Failed to load checklist details:', error);
    } finally {
      setLoadingChecklists(false);
    }
  };

  const handleItemChange = (itemId: number, value: any) => {
    setFormData((prev) => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: value,
      },
    }));
  };

  const renderCountStepper = (item: CategoryNode, value: any, max: number) => {
    const current = typeof value === 'number' ? value : 0;
    const label = simpleName(item);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            aria-label={`Decrease ${label}`}
            onClick={() => handleItemChange(item.id, Math.max(0, current - 1))}
            sx={{ width: 44, height: 44 }}
          >
            <Remove />
          </IconButton>
          <TextField
            value={current}
            onChange={(e) => {
              const next = parseInt(e.target.value, 10);
              handleItemChange(item.id, Number.isNaN(next) ? 0 : Math.min(max, Math.max(0, next)));
            }}
            inputProps={{
              inputMode: 'numeric',
              min: 0,
              max,
              style: { textAlign: 'center', fontSize: '1.1rem', fontWeight: 700 },
            }}
            sx={{ width: 64, '& .MuiInputBase-input': { py: 1 } }}
            size="small"
          />
          <IconButton
            aria-label={`Increase ${label}`}
            onClick={() => handleItemChange(item.id, Math.min(max, current + 1))}
            sx={{ width: 44, height: 44 }}
          >
            <Add />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderChipSelect = (
    item: CategoryNode,
    value: any,
    options: { value: string; label: string }[],
  ) => (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        {simpleName(item)}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {options.map((option) => (
          <Chip
            key={option.value}
            label={SIMPLE_CHOICE_LABELS[option.value] || SIMPLE_CHOICE_LABELS[option.label.toLowerCase()] || option.label}
            clickable
            color={value === option.value ? 'primary' : 'default'}
            variant={value === option.value ? 'filled' : 'outlined'}
            onClick={() => handleItemChange(item.id, option.value)}
            sx={{ minHeight: 40, px: 0.5 }}
          />
        ))}
      </Box>
    </Box>
  );

  const renderInput = (item: CategoryNode) => {
    const value = formData.items[item.id];
    const isRequired = item.is_required;
    const slug = item.slug || '';

    if (FRAME_SLUGS.has(slug) || item.input_type === 'number') {
      const rangeOptions = !Array.isArray(item.input_options) ? (item.input_options as any) : undefined;
      const numberMax = typeof rangeOptions?.max === 'number' ? rangeOptions.max : FRAME_SLUGS.has(slug) ? 20 : 100;
      if (FRAME_SLUGS.has(slug) || numberMax <= 30) {
        return renderCountStepper(item, value, numberMax);
      }
      return (
        <TextField
          fullWidth
          type="number"
          size="small"
          label={`${simpleName(item)}${isRequired ? ' *' : ''}`}
          value={value ?? ''}
          onChange={(e) => handleItemChange(item.id, e.target.value ? parseFloat(e.target.value) : null)}
        />
      );
    }

    switch (item.input_type) {
      case 'boolean':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {simpleName(item)} {isRequired && '*'}
            </Typography>
            <YesNoRating
              value={value}
              onChange={(newValue) => handleItemChange(item.id, newValue)}
              label=""
              showLabels
              size="large"
            />
          </Box>
        );

      case 'star':
      case 'rating':
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {simpleName(item)} {isRequired && '*'}
            </Typography>
            <StarRating
              value={value || 0}
              onChange={(newValue) => handleItemChange(item.id, newValue)}
              max={5}
              showValue={false}
              size={isMobile ? 'large' : 'medium'}
            />
          </Box>
        );

      case 'smile':
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {simpleName(item)} {isRequired && '*'}
            </Typography>
            <SmileRating
              value={value || 3}
              onChange={(newValue) => handleItemChange(item.id, newValue)}
              size="large"
            />
          </Box>
        );

      case 'select': {
        const configured = Array.isArray(item.input_options) ? item.input_options : [];
        const options = (configured.length > 0 ? configured : FALLBACK_PEST_OPTIONS[slug] || [
          { value: 'none', label: 'None' },
          { value: 'few', label: 'Few' },
          { value: 'many', label: 'Many' },
        ]).map((option: any) => ({
          value: typeof option === 'object' && option !== null ? String(option.value ?? option.label ?? option.name) : String(option),
          label: typeof option === 'object' && option !== null ? String(option.label ?? option.name ?? option.value) : String(option),
        }));
        return renderChipSelect(item, value, options);
      }

      case 'text':
        return (
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={`${simpleName(item)}${isRequired ? ' *' : ''}`}
            value={value || ''}
            onChange={(e) => handleItemChange(item.id, e.target.value)}
          />
        );

      default:
        return (
          <TextField
            fullWidth
            label={`${simpleName(item)}${isRequired ? ' *' : ''}`}
            value={value || ''}
            onChange={(e) => handleItemChange(item.id, e.target.value)}
          />
        );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const inspectionData = {
        ...formData,
        images: inspectionImages.map((img) => img.file),
      };

      const results = [];
      for (const selectedHiveId of formData.hive_ids) {
        try {
          let response;
          if (inspectionImages.length > 0) {
            const formDataObj = new FormData();

            formDataObj.append('hive', selectedHiveId.toString());
            if (inspectionData.checklist_id) {
              formDataObj.append('template', inspectionData.checklist_id.toString());
            }
            formDataObj.append('inspection_date', inspectionData.date.toISOString());
            formDataObj.append('notes', inspectionData.notes);
            formDataObj.append('weather_conditions', inspectionData.weather);
            if (inspectionData.temperature !== null) {
              formDataObj.append('weather_temperature', inspectionData.temperature.toString());
            }
            formDataObj.append('impression', inspectionData.impression.toString());
            if (inspectionData.reminder) {
              formDataObj.append('reminder_date', inspectionData.reminder.toISOString());
              formDataObj.append('reminder', inspectionData.reminder_notes || 'Inspection Reminder');
            }
            formDataObj.append('inspection_data', JSON.stringify(inspectionData.items));

            inspectionImages.forEach((img) => {
              formDataObj.append('images', img.file);
            });

            response = await ApiService.upload('inspections/', formDataObj);
          } else {
            const jsonData = {
              hive: selectedHiveId,
              template: inspectionData.checklist_id,
              inspection_date: inspectionData.date,
              notes: inspectionData.notes,
              weather_conditions: inspectionData.weather,
              weather_temperature: inspectionData.temperature,
              impression: inspectionData.impression,
              reminder_date: inspectionData.reminder,
              reminder: inspectionData.reminder_notes,
              inspection_data: inspectionData.items,
            };
            response = await ApiService.post('inspections/', jsonData);
          }
          results.push({ hiveId: selectedHiveId, success: true, response });
        } catch (error) {
          results.push({ hiveId: selectedHiveId, success: false, error });
        }
      }

      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (failCount === 0) {
        setMessage({ type: 'success', text: `${successCount} inspection(s) created successfully!` });
      } else if (successCount > 0) {
        setMessage({
          type: 'error',
          text: `${successCount} succeeded, ${failCount} failed. Check console for details.`,
        });
      } else {
        setMessage({
          type: 'error',
          text: `Failed to save ${failCount === 1 ? 'the inspection' : 'any inspections'}. Please check your entries and try again.`,
        });
      }

      if (successCount > 0) {
        setTimeout(() => {
          navigate('/inspections');
        }, 2000);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to create inspection',
      });
    } finally {
      setLoading(false);
    }
  };

  const weatherOptions = [
    { value: 'sunny', label: '☀️ Sunny' },
    { value: 'partly_cloudy', label: '⛅ Partly Cloudy' },
    { value: 'cloudy', label: '☁️ Cloudy' },
    { value: 'rainy', label: '🌧️ Rainy' },
    { value: 'stormy', label: '⛈️ Stormy' },
    { value: 'snowy', label: '🌨️ Snowy' },
  ];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 1, md: 2 }, px: { xs: 1.5, sm: 3 }, pb: { xs: 10, md: 4 } }}>
      <SaveToolbar
        onSave={handleSubmit}
        saveDisabled={loading || !selectedChecklist || formData.hive_ids.length === 0}
        loading={loading}
        showDelete={false}
      />

      <Box sx={{ mb: 2, mt: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom>
          New Inspection
        </Typography>
        {selectedHives.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {selectedHives.length} {selectedHives.length === 1 ? 'hive' : 'hives'} selected
          </Typography>
        )}
      </Box>

      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <CalendarToday sx={{ mr: 0.5, fontSize: '0.9rem' }} />
            Date of Inspection *
          </Typography>
          <DateTimePicker
            value={formData.date}
            onChange={handleDateChange('date')}
            label="Inspection Date"
            showTime
            maxDate={new Date()}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <ApiarySelector
            selectedApiaryId={selectedApiaryId}
            onApiaryChange={setSelectedApiaryId}
            label="Select apiary"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          {!loadingChecklists && checklists.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Select Checklist</InputLabel>
              <Select
                value={formData.checklist_id || ''}
                onChange={(e) => handleChecklistChange(e.target.value as number)}
                label="Select Checklist"
              >
                {checklists.map((checklist) => (
                  <MenuItem key={checklist.id} value={checklist.id}>
                    {checklist.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
          Hives
        </Typography>
        <HiveSelector
          selectedHives={formData.hive_ids}
          onSelectionChange={(hiveIds) => setFormData((prev) => ({ ...prev, hive_ids: hiveIds }))}
          selectedApiaryId={selectedApiaryId}
        />
      </Card>

      <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1.5, fontWeight: 600 }}>
          <Cloud sx={{ mr: 1, fontSize: '1.1rem' }} />
          Weather & General Info
        </Typography>

        <Typography variant="caption" color="text.secondary">Weather</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2, mt: 0.5 }}>
          {weatherOptions.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              color={formData.weather === option.value ? 'primary' : 'default'}
              variant={formData.weather === option.value ? 'filled' : 'outlined'}
              onClick={() => setFormData((prev) => ({ ...prev, weather: option.value }))}
              sx={{ minHeight: 40 }}
            />
          ))}
        </Box>

        <TextField
          fullWidth
          size="small"
          type="number"
          label="Temperature (°C)"
          name="temperature"
          value={formData.temperature || ''}
          onChange={handleInputChange}
          InputProps={{
            startAdornment: <Thermostat sx={{ mr: 1, color: 'text.secondary', fontSize: '1.1rem' }} />,
          }}
          sx={{ mb: 2 }}
        />

        <Typography variant="caption" color="text.secondary">Overall Impression</Typography>
        <Box sx={{ '& .MuiTypography-root': { display: { xs: 'none', sm: 'block' } } }}>
          <SmileRating
            value={formData.impression}
            onChange={(value) => setFormData((prev) => ({ ...prev, impression: value }))}
            label=""
            size="large"
          />
        </Box>
      </Card>

      <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, mb: 1 }}>
          <ChecklistRtl sx={{ mr: 1, fontSize: '1.1rem' }} />
          Inspection Checklist
        </Typography>

        {loadingChecklists ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : !selectedChecklist ? (
          <Alert severity="info" variant="outlined">
            Please select a checklist to begin the inspection.
          </Alert>
        ) : (
          <Box>
            {selectedChecklist.description && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                {selectedChecklist.description}
              </Typography>
            )}

            {groupedChecklist.map(({ root, leaves }) => (
              <Box key={root.id} sx={{ mb: 2.5 }}>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, letterSpacing: 0.6, color: root.color || 'text.secondary' }}
                >
                  {simpleName(root)}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    mt: 0.75,
                    p: { xs: 1.25, md: 1.5 },
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  {leaves.map((item) => (
                    <Box key={item.id}>{renderInput(item)}</Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Card>

      <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="Notes"
          name="notes"
          value={formData.notes}
          onChange={handleInputChange}
          placeholder="Add any general observations..."
        />
      </Card>

      <Accordion disableGutters elevation={0} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Set Reminder (Optional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DateTimePicker
            value={formData.reminder}
            onChange={handleDateChange('reminder')}
            label="Reminder Date"
            showTime
            minDate={new Date()}
          />
          {formData.reminder && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label="Reminder notes"
              name="reminder_notes"
              value={formData.reminder_notes}
              onChange={handleInputChange}
              margin="dense"
              placeholder="What should you check next time?"
            />
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 48 }}>
          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
            <PhotoCamera sx={{ mr: 1, fontSize: '1.1rem' }} />
            Inspection Images
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ImageUploader
            label="Upload Inspection Images"
            multiple
            maxFiles={10}
            maxSize={10}
            onImagesChange={setInspectionImages}
            initialImages={inspectionImages}
          />
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default InspectionCreate;
